import type { ApprovalReason, Currency, EstimateInputs, MonitoringRates, PricingResult } from '@atek/estimate-pricing';

export type EstimateStatus = 'draft' | 'pending_approval' | 'approved' | 'presented' | 'converted' | 'lost' | 'superseded';
export const ESTIMATE_STATUSES: readonly EstimateStatus[] = ['draft', 'pending_approval', 'approved', 'presented', 'converted', 'lost', 'superseded'];
export type DeckStatus = 'idle' | 'generating' | 'failed';
export type Lang = 'en' | 'fr';
export type LifecycleAction = 'request-approval' | 'approve' | 'reject' | 'mark-presented' | 'mark-lost';

export interface Presenter { name: string; title: string; phone: string; email: string }
export interface ContextBlock { heading: string; body: string }
export interface DeckWarning { code: string; slide?: number | null; objectId?: string | null; message: string }

/** An estimate as GET /v4/estimates/:id returns it (rest_server serializes the row in camelCase). */
export interface Estimate {
  id: string;
  number: string;
  revision: number;
  supersedesId: string | null;
  opportunityId: string;
  companyId: string | null;
  accountId: string | null;
  offerType: 'monitoring';
  priceBookId: string;
  currency: Currency;
  fxRate: number;
  title: string;
  language: Lang;
  validUntil: string;
  status: EstimateStatus;
  inputs: EstimateInputs;
  result: PricingResult;
  businessContext: ContextBlock[];
  presenter: Presenter;
  coverContactId: string | null;
  siteAddress: string | null;
  approvalReasons: ApprovalReason[];
  rejectionComment: string | null;
  presentedAt: string | null;
  deckStatus: DeckStatus;
  deckFileId: string | null;
  deckUrl: string | null;
  deckGeneratedAt: string | null;
  deckClaimedAt: string | null;
  deckWarnings: DeckWarning[];
  deckError: string | null;
  chosenBudgetKey: string | null;
  quoteId: string | null;
  convertedAt: string | null;
  ownerUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EstimateCompany {
  id: string;
  name: string;
  accountId: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string | null;
  domain: string | null;
  logoUrl: string | null;
}

export interface EstimatePriceBook { id: string; version: number; status: 'draft' | 'published' | 'retired'; rates: MonitoringRates }

export interface EstimateDetail {
  estimate: Estimate;
  priceBook: EstimatePriceBook | null;
  company: EstimateCompany | null;
  stalePriceBook: boolean;
  latestPriceBookVersion: number | null;
}

/** A row of GET /v4/estimates. */
export interface EstimateSummary {
  id: string;
  number: string;
  revision: number;
  status: EstimateStatus;
  title: string;
  opportunityId: string;
  companyId: string | null;
  accountId: string | null;
  ownerUserId: string | null;
  currency: Currency;
  language: Lang;
  validUntil: string;
  deckUrl: string | null;
  quoteId: string | null;
  approvalReasonCount: number;
  budgets: { key: string; label: string; year1: number; year5: number }[];
  createdAt: string;
  updatedAt: string;
  companyName: string | null;
  opportunityTitle: string | null;
}

export interface EstimateListFilters {
  opportunityId?: string;
  status?: EstimateStatus[];
  ownerUserId?: string;
  awaitingApproval?: boolean;
  pageNumber?: number;
  pageLength?: number;
}

export interface EstimateList { data: EstimateSummary[]; total: number; pageNumber: number; pageLength: number }

export interface SaveEstimateBody {
  expectedUpdatedAt: string;
  title?: string;
  language?: Lang;
  validUntil?: string;
  siteAddress?: string | null;
  coverContactId?: string | null;
  businessContext?: ContextBlock[];
  presenter?: Presenter;
  inputs?: EstimateInputs;
}

/** The POST /v4/quotes body built by GET /v4/estimates/:id/quote-draft. Sent back unchanged. */
export interface QuoteDraftPayload {
  accountId: string;
  opportunityId: string;
  projectName: string;
  currency: string;
  total: number;
  lineItems: unknown[];
  [key: string]: unknown;
}

export interface CreatedQuote { id: string; quoteNumber?: number | string | null; lineItems?: unknown[] }

export interface DistanceResult { status: 'ok' | 'unavailable' | 'invalid' | 'not_found'; km: number | null; reason?: string; origin?: string }

export interface TemplateValidation { title: string | null; checkedAt: string; slideCount: number; errors: DeckWarning[]; warnings: DeckWarning[] }

export interface ProposalTemplate {
  id: string;
  offerType: 'monitoring';
  language: Lang;
  driveFileId: string;
  name: string | null;
  lastValidatedAt: string | null;
  lastValidation: TemplateValidation | null;
  updatedBy: string | null;
  updatedAt: string;
}
